import { LayerData, defaultLayerSettings, layerSettingsAtom, tLayerSettings } from '@/atoms/layers.atom';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { execute } from '@/graphql/execute';
import { useLayerSettings } from '@/hooks/useLayerSettings';
import { useDialog } from '@/providers/DialogProvider';
import { updateLayerSettingsMutation } from '@/queries/layer.query';
import { useFormik } from 'formik';
import { useSetAtom } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';

interface ViewerSettingsDialogProps {
  layer: LayerData & { subLayers: LayerData[] };
  isEditor: boolean;
}

const ClassificationItem = memo(
  ({
    index,
    isEnabled,
    color,
    onToggle,
    onColorChange,
  }: {
    index: number;
    isEnabled: boolean;
    color: string;
    onToggle: (index: number) => void;
    onColorChange: (index: number, color: string) => void;
  }) => {
    const _handleToggle = useCallback(() => {
      onToggle(index);
    }, [index, onToggle]);

    const _handleColorChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onColorChange(index, e.target.value);
      },
      [index, onColorChange],
    );

    return (
      <div className='flex gap-2 items-center border-b last:border-b-0 border-slate-200/70 pl-3 py-2 dark:border-stone-700/40'>
        <Checkbox checked={isEnabled} onCheckedChange={_handleToggle} />
        <input
          type='color'
          id={`point-cloud-material-color-${index}`}
          className='size-8 rounded-full border-none'
          value={color}
          onChange={_handleColorChange}
        />
        <Label htmlFor={`point-cloud-material-color-${index}`}>Classification #{index}</Label>
      </div>
    );
  },
);

ClassificationItem.displayName = 'ClassificationItem';

export const DebouncedColorInput = memo(
  ({
    value,
    onChange,
    id,
    className,
  }: {
    value: string;
    onChange: (value: string) => void;
    id: string;
    className: string;
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const timeoutRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const _handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => {
          onChange(newValue);
        }, 150); // 150ms debounce
      },
      [onChange],
    );

    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          window.clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    return <input type='color' id={id} className={className} value={localValue} onChange={_handleChange} />;
  },
);

DebouncedColorInput.displayName = 'DebouncedColorInput';

export function LayerSettingsDialog({ layer, isEditor }: ViewerSettingsDialogProps) {
  const { id: layerId, type: layerType } = layer;

  const setLayerSettings = useSetAtom(layerSettingsAtom(layerId));
  const { settings: layerSettings } = useLayerSettings(layerId);
  const dialog = useDialog();

  const formik = useFormik<tLayerSettings>({
    initialValues: {
      ...defaultLayerSettings,
      ...(layerSettings || {}),
    },
    onSubmit: async (values) => {
      setLayerSettings(values);
      dialog.dismissActiveDialog();
    },
  });

  useEffect(() => {
    if (layerSettings) formik.setValues(layerSettings as any);
  }, [layerSettings]);

  const _handleClose = useCallback(() => {
    dialog.dismissActiveDialog();
  }, [dialog]);

  const _handleSaveForEveryone = async () => {
    toast.promise(
      execute(updateLayerSettingsMutation, {
        id: layerId,
        input: formik.values,
      }),
      {
        loading: 'Saving settings for everyone...',
        success: 'Settings saved for everyone',
        error: 'Failed to save settings for everyone',
      },
    );
    formik.submitForm();
  };

  const numberOfClasses =
    (layer.metadata?.potreeMetadata?.attributes.find((attribute: any) => attribute.name === 'classification')?.max[0] ||
      255) + 1;

  const _handleClassificationToggle = useCallback(
    (index: number) => {
      const newClasses = [...(formik.values.pointCloudClasses || [])];
      if (!newClasses[index]) {
        newClasses[index] = { isEnabled: false, color: '#000000' };
      }
      newClasses[index] = {
        ...newClasses[index],
        isEnabled: !newClasses[index].isEnabled,
      };
      formik.setFieldValue('pointCloudClasses', newClasses);
    },
    [formik],
  );

  const _handleClassificationColorChange = useCallback(
    (index: number, color: string) => {
      const newClasses = [...(formik.values.pointCloudClasses || [])];
      if (!newClasses[index]) {
        newClasses[index] = { isEnabled: false, color: '#000000' };
      }
      newClasses[index] = {
        ...newClasses[index],
        color,
      };
      formik.setFieldValue('pointCloudClasses', newClasses);
    },
    [formik],
  );

  // Memoize model color change handler
  const _handleModelColorChange = useCallback(
    (color: string) => {
      formik.setFieldValue('modelDefaultColor', color);
    },
    [formik],
  );

  // Memoize the classification list to prevent unnecessary re-renders
  const classificationItems = useMemo(() => {
    return Array.from({ length: numberOfClasses }).map((_, index) => {
      const classification = formik.values.pointCloudClasses?.[index];
      return (
        <ClassificationItem
          key={index}
          index={index}
          isEnabled={classification?.isEnabled || false}
          color={classification?.color || '#000000'}
          onToggle={_handleClassificationToggle}
          onColorChange={_handleClassificationColorChange}
        />
      );
    });
  }, [formik.values.pointCloudClasses, _handleClassificationToggle, _handleClassificationColorChange, numberOfClasses]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>Layer Settings</DialogTitle>
        <DialogDescription>Configure your layer preferences</DialogDescription>
      </DialogHeader>
      <form onSubmit={formik.handleSubmit}>
        <div className='grid gap-6 py-4 max-h-[60vh] overflow-auto'>
          {layerType === 'POINTCLOUD' && (
            <>
              <div className='grid grid-cols-2 gap-6'>
                <div className='flex flex-col gap-2'>
                  <Label htmlFor='point-cloud-material-color-source'>Material color source</Label>
                  <Select
                    value={formik.values.pointCloudMaterialColorSource.toString()}
                    onValueChange={(value) => formik.setFieldValue('pointCloudMaterialColorSource', parseInt(value))}>
                    <SelectTrigger className='w-full'>
                      <SelectValue placeholder='Select a material color source' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'rgb',
                        )}
                        value='0'>
                        RGB
                      </SelectItem>
                      {/* <SelectItem value='2'>Depth</SelectItem> */}
                      <SelectItem value='3'>Elevation</SelectItem>
                      {/* <SelectItem value='4'>Intensity</SelectItem> */}
                      <SelectItem value='5'>Intensity Gradient</SelectItem>
                      {/* <SelectItem value='6'>LOD</SelectItem> */}
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'classification',
                        )}
                        value='8'>
                        Classification
                      </SelectItem>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'return number',
                        )}
                        value='9'>
                        Return number
                      </SelectItem>
                      <SelectItem
                        disabled={layer.metadata.potreeMetadata.attributes.every(
                          (attribute: any) => attribute.name !== 'point source id',
                        )}
                        value='10'>
                        Source ID
                      </SelectItem>
                      {/* <SelectItem value='13'>RGB Height</SelectItem> */}
                      <SelectItem value='1'>Fixed Color</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {['3', '5'].includes(formik.values.pointCloudMaterialColorSource.toString()) ? (
                  <div className='flex flex-col gap-2'>
                    <Label htmlFor='point-cloud-material-color'>Material color</Label>
                    <Select
                      value={formik.values.pointCloudMaterialColor}
                      onValueChange={(value) => formik.setFieldValue('pointCloudMaterialColor', value)}>
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select a material color' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='GRAYSCALE'>Grayscale</SelectItem>
                        <SelectItem value='RAINBOW'>Rainbow</SelectItem>
                        <SelectItem value='SPECTRAL'>Spectral</SelectItem>
                        <SelectItem value='PLASMA'>Plasma</SelectItem>
                        <SelectItem value='YELLOW_GREEN'>Yellow Green</SelectItem>
                        <SelectItem value='VIRIDIS'>Viridis</SelectItem>
                        <SelectItem value='INFERNO'>Inferno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>
              {formik.values.pointCloudMaterialColorSource === 1 && (
                <div className='flex items-center gap-2'>
                  <DebouncedColorInput
                    id='point-cloud-fixed-color'
                    className='size-8 rounded-full border-none'
                    value={formik.values.pointCloudFixedColor}
                    onChange={(value) => formik.setFieldValue('pointCloudFixedColor', value)}
                  />
                  <Label htmlFor='point-cloud-fixed-color'>Fixed color</Label>
                </div>
              )}
              {layer.metadata.potreeMetadata.attributes.some((attribute: any) => attribute.name === 'classification') &&
                formik.values.pointCloudMaterialColorSource === 8 && (
                  <div className='flex flex-col gap-2'>
                    <Label htmlFor='point-cloud-material-color'>Classifications</Label>
                    <div className='flex flex-col border border-slate-200/70 rounded-md bg-slate-50/40 dark:bg-stone-900/40 dark:border-stone-700/40'>
                      {classificationItems}
                    </div>
                  </div>
                )}
            </>
          )}
          {layerType === 'MODEL' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='model-default-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.modelDefaultColor}
                  onChange={_handleModelColorChange}
                />
                <Label htmlFor='model-default-color'>Default color</Label>
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='model-is-wireframe'
                  checked={formik.values.modelIsWireframe}
                  onCheckedChange={(checked) => formik.setFieldValue('modelIsWireframe', checked)}
                />
                <Label htmlFor='model-is-wireframe'>Show wireframe</Label>
              </div>
            </>
          )}
          {layerType === 'IMAGE' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='image-point-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.imagePointColor}
                  onChange={(value) => formik.setFieldValue('imagePointColor', value)}
                />
                <Label htmlFor='image-point-color'>Image POI color</Label>
              </div>
            </>
          )}
          {layerType === 'VECTOR' && (
            <>
              <div className='flex items-center gap-2'>
                <DebouncedColorInput
                  id='vector-default-color'
                  className='size-8 rounded-full border-none'
                  value={formik.values.vectorDefaultColor}
                  onChange={(value) => formik.setFieldValue('vectorDefaultColor', value)}
                />
                <Label htmlFor='vector-default-color'>Vector default color</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='vector-default-opacity whitespace-nowrap'>
                  Vector default opacity{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {(formik.values.vectorDefaultOpacity * 100).toFixed(0)}%
                  </span>
                </Label>
                <Slider
                  id='vector-default-opacity'
                  value={[formik.values.vectorDefaultOpacity * 100]}
                  min={0}
                  max={100}
                  onValueChange={(value) => formik.setFieldValue('vectorDefaultOpacity', value[0] / 100)}
                />
              </div>
              <div className='flex items-center gap-2'>
                <Switch
                  id='vector-is-wireframe'
                  checked={formik.values.vectorIsWireframe}
                  onCheckedChange={(checked) => formik.setFieldValue('vectorIsWireframe', checked)}
                />
                <Label htmlFor='vector-is-wireframe'>Show wireframe</Label>
              </div>
              <div className='flex flex-col gap-2'>
                <Label htmlFor='vector-default-point-size whitespace-nowrap'>
                  Vector default point size{' '}
                  <span className='text-xs font-medium text-muted-foreground font-mono'>
                    {Number(formik.values.vectorDefaultPointSize * 100).toFixed(0)} cm
                  </span>
                </Label>
                <Slider
                  id='vector-default-opacity'
                  value={[formik.values.vectorDefaultPointSize]}
                  min={0}
                  step={0.01}
                  max={1}
                  onValueChange={(value) => formik.setFieldValue('vectorDefaultPointSize', Number(value[0].toFixed(2)))}
                />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={_handleClose}>
            Cancel
          </Button>
          {isEditor && (
            <Button type='button' variant='outline' onClick={_handleSaveForEveryone}>
              Save For Everyone
            </Button>
          )}
          <Button type='submit'>Save Settings</Button>
        </DialogFooter>
      </form>
    </>
  );
}

LayerSettingsDialog.displayName = 'LayerSettingsDialog';
